import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { authService } from '../services/authService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [authError, setAuthError] = useState(null)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) {
        console.warn('Profile load called without userId');
        return;
      }

      setProfileLoading(true)
      try {
        if (!supabase || !supabase?.from) {
          throw new Error('Supabase client not initialized');
        }

        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

        if (error) {
          console.error('Profile load error:', error);
          setAuthError(`Failed to load user profile: ${error?.message}`);
          return;
        }

        if (!data) {
          console.warn('No profile data found for user:', userId);
          setAuthError('User profile not found. Please contact support.');
          return;
        }

        setUserProfile(data);
        setAuthError(null);
        
        // Check and apply pending OAuth role if exists
        await authService?.applyPendingOAuthRole(userId);
      } catch (error) {
        console.error('Profile load error:', error);
        setAuthError(`Profile loading failed: ${error?.message}`);
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
      setAuthError(null)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      console.log('Auth state changed:', event, session ? 'Session exists' : 'No session');

      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        console.log('User authenticated:', session?.user?.id);
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        console.log('User signed out or no session');
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Verify Supabase client is initialized
    if (!supabase || !supabase?.auth) {
      console.error('Supabase client not initialized properly');
      setAuthError('Authentication service unavailable. Please check configuration.');
      setLoading(false);
      return;
    }

    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      if (error) {
        console.error('Initial session check error:', error);
        setAuthError(`Session check failed: ${error?.message}`);
      }
      authStateHandlers?.onChange(null, session)
    })?.catch((error) => {
      console.error('Failed to get initial session:', error);
      setAuthError('Failed to initialize authentication');
      setLoading(false);
    });

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => {
      if (subscription && subscription?.unsubscribe) {
        subscription?.unsubscribe();
      }
    };
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      setAuthError(null);

      if (!email || !password) {
        const error = { message: 'Email and password are required' };
        setAuthError(error?.message);
        return { data: null, error };
      }

      if (!supabase || !supabase?.auth) {
        const error = { message: 'Authentication service unavailable' };
        setAuthError(error?.message);
        return { data: null, error };
      }

      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });

      if (error) {
        console.error('Sign in error:', error);
        setAuthError(error?.message);
        return { data: null, error };
      }

      if (!data || !data?.user) {
        const error = { message: 'Sign in failed: No user data returned' };
        setAuthError(error?.message);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign in exception:', error);
      const errorMessage = error?.message || 'Network error. Please try again.';
      setAuthError(errorMessage);
      return { data: null, error: { message: errorMessage } };
    }
  }

  const signUp = async (email, password, fullName, role = 'user') => {
    try {
      setAuthError(null);

      if (!email || !password || !fullName) {
        const error = { message: 'All fields are required' };
        setAuthError(error?.message);
        return { data: null, error };
      }

      if (!supabase || !supabase?.auth) {
        const error = { message: 'Authentication service unavailable' };
        setAuthError(error?.message);
        return { data: null, error };
      }

      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role
          }
        }
      });

      if (error) {
        console.error('Sign up error:', error);
        setAuthError(error?.message);
        return { data: null, error };
      }

      if (!data || !data?.user) {
        const error = { message: 'Sign up failed: No user data returned' };
        setAuthError(error?.message);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign up exception:', error);
      const errorMessage = error?.message || 'Network error. Please try again.';
      setAuthError(errorMessage);
      return { data: null, error: { message: errorMessage } };
    }
  }

  const signInWithGoogle = async (role = 'user') => {
    try {
      setAuthError(null);

      if (!supabase || !supabase?.auth) {
        const error = { message: 'Authentication service unavailable' };
        setAuthError(error?.message);
        return { data: null, error };
      }

      const { data, error } = await authService?.signInWithGoogle(role);

      if (error) {
        console.error('Google sign in error:', error);
        setAuthError(error?.message);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Google sign in exception:', error);
      const errorMessage = error?.message || 'Network error. Please try again.';
      setAuthError(errorMessage);
      return { data: null, error: { message: errorMessage } };
    }
  }

  const signOut = async () => {
    try {
      setAuthError(null);

      if (!supabase || !supabase?.auth) {
        const error = { message: 'Authentication service unavailable' };
        setAuthError(error?.message);
        return { error };
      }

      const { error } = await supabase?.auth?.signOut();

      if (error) {
        console.error('Sign out error:', error);
        setAuthError(error?.message);
        return { error };
      }

      setUser(null);
      profileOperations?.clear();
      return { error: null };
    } catch (error) {
      console.error('Sign out exception:', error);
      const errorMessage = error?.message || 'Network error. Please try again.';
      setAuthError(errorMessage);
      return { error: { message: errorMessage } };
    }
  }

  const updateProfile = async (updates) => {
    if (!user) {
      const error = { message: 'No user logged in' };
      setAuthError(error?.message);
      return { data: null, error };
    }
    
    try {
      setAuthError(null);

      if (!supabase || !supabase?.from) {
        const error = { message: 'Database service unavailable' };
        setAuthError(error?.message);
        return { data: null, error };
      }

      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();

      if (error) {
        console.error('Update profile error:', error);
        setAuthError(error?.message);
        return { data: null, error };
      }

      if (data) {
        setUserProfile(data);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Update profile exception:', error);
      const errorMessage = error?.message || 'Network error. Please try again.';
      setAuthError(errorMessage);
      return { data: null, error: { message: errorMessage } };
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    authError,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}